// useFleetOps — all fleet *mutation* UX in one hook: add / resize / remove a
// host, edit fleet config, configure + activate SSO, and a jobs drawer that
// streams each long op's live log. The read-only Fleet page (FleetPage)
// stays a clean render; it calls these openers from its toolbar + per-host menu
// and drops {portals} at the end. Every long op returns { job_id } and is
// tracked here → the jobs drawer polls it to success/failure.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Drawer, Modal, Form, InputNumber, Input, Select, Checkbox, Button, Flex, message, Popconfirm, theme, Empty } from "antd";
import { Plus, Settings, ShieldCheck, ListChecks, Loader2, CheckCircle2, XCircle, Trash2, KeyRound } from "lucide-react";
import { fleetApi, type Job, type Size, type FleetConfig, type SsoConfig, SIZE_LABEL, SIZE_CANDIDATES } from "@/lib/api/fleet";
import { HintIcon } from "@/components/agate";
import { Tag } from "@/shared/ui";

const MONO = "var(--font-mono, 'JetBrains Mono Variable', monospace)";
const activeState = (s: Job["state"]) => s === "queued" || s === "running";

export function useFleetOps(onChanged: () => void) {
  const { token } = theme.useToken();
  const [sizes, setSizes] = useState<Size[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsOpen, setJobsOpen] = useState(false);
  const [sizeTarget, setSizeTarget] = useState<{ mode: "add" } | { mode: "resize"; name: string; current?: string } | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ssoOpen, setSsoOpen] = useState(false);
  const tracked = useRef<Set<string>>(new Set());

  const activeCount = jobs.filter((j) => activeState(j.state)).length;

  const refreshJobs = useCallback(async () => {
    try {
      setJobs(await fleetApi.jobs());
    } catch {
      /* transient */
    }
  }, []);

  // poll while any job is active OR the drawer is open (so a just-finished job's
  // final log lands), then idle.
  useEffect(() => {
    if (!jobsOpen && activeCount === 0) return;
    const t = window.setInterval(refreshJobs, 2500);
    return () => window.clearInterval(t);
  }, [jobsOpen, activeCount, refreshJobs]);

  // when an op finishes, refresh the fleet view once.
  const prevActive = useRef(0);
  useEffect(() => {
    if (prevActive.current > 0 && activeCount === 0) onChanged();
    prevActive.current = activeCount;
  }, [activeCount, onChanged]);

  const track = useCallback(
    async (start: Promise<{ job_id: string }>, label: string) => {
      try {
        const { job_id } = await start;
        tracked.current.add(job_id);
        message.success(`${label} started`);
        setJobsOpen(true);
        refreshJobs();
      } catch (e) {
        message.error(e instanceof Error ? e.message : `${label} failed`);
      }
    },
    [refreshJobs],
  );

  const loadSizes = useCallback(async () => {
    try {
      setSizes(await fleetApi.sizes());
    } catch {
      setSizes([]);
    }
  }, []);

  const openAddHost = useCallback(() => { loadSizes(); setSizeTarget({ mode: "add" }); }, [loadSizes]);
  const resizeHost = useCallback((name: string, current?: string) => { loadSizes(); setSizeTarget({ mode: "resize", name, current }); }, [loadSizes]);
  const removeHost = useCallback((name: string) => track(fleetApi.removeNode(name), `Remove ${name}`), [track]);
  const openSettings = useCallback(() => setSettingsOpen(true), []);
  const openSso = useCallback(() => setSsoOpen(true), []);
  const openJobs = useCallback(() => { setJobsOpen(true); refreshJobs(); }, [refreshJobs]);

  const submitSize = useCallback(
    async (size: string, sandboxes?: number) => {
      if (!sizeTarget) return;
      // Apply the (possibly-overridden) sandbox capacity for the chosen size.
      // Non-fatal: the add/resize still proceeds if the config write fails.
      if (typeof sandboxes === "number" && sandboxes > 0) {
        try { await fleetApi.putConfig({ max_sandboxes_per_node: sandboxes }); } catch { /* non-fatal */ }
      }
      if (sizeTarget.mode === "add")
        await track(fleetApi.addNode({ size }), `Add host (${size}${sandboxes ? `, ${sandboxes} sandboxes` : ""})`);
      else
        await track(fleetApi.resizeNode(sizeTarget.name, size), `Resize ${sizeTarget.name} → ${size}`);
      setSizeTarget(null);
    },
    [sizeTarget, track],
  );

  const portals = (
    <>
      <SizeModal target={sizeTarget} sizes={sizes} onCancel={() => setSizeTarget(null)} onSubmit={submitSize} />
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={onChanged} />
      <SsoDrawer open={ssoOpen} onClose={() => setSsoOpen(false)} />
      <JobsDrawer open={jobsOpen} jobs={jobs} onClose={() => setJobsOpen(false)} token={token} />
    </>
  );

  return { openAddHost, resizeHost, removeHost, openSettings, openSso, openJobs, activeCount, portals };
}

// ── size modal (add host / resize host) ──────────────────────────────────────
function SizeModal({
  target, sizes, onCancel, onSubmit,
}: {
  target: { mode: "add" } | { mode: "resize"; name: string; current?: string } | null;
  sizes: Size[];
  onCancel: () => void;
  onSubmit: (size: string, sandboxes?: number) => Promise<void>;
}) {
  const [size, setSize] = useState<string | undefined>();
  const [chosenType, setChosenType] = useState<string | undefined>();
  const [allowFallback, setAllowFallback] = useState(true);
  const [sandboxes, setSandboxes] = useState<number | undefined>();
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSize(undefined); setChosenType(undefined); setAllowFallback(true); setSandboxes(undefined); }, [target]);
  const isResize = target?.mode === "resize";
  // Selecting a size defaults the exact type to its best candidate + prefills
  // the recommended sandbox count (operator can override both).
  const onSizeChange = (s: string) => {
    setSize(s);
    setChosenType(SIZE_CANDIDATES[s]?.[0]);
    const rec = sizes.find((x) => x.size === s)?.sandboxes;
    setSandboxes(typeof rec === "number" ? rec : undefined);
  };
  // What fleetctl receives: the chosen type first, then the size's other
  // candidates as fallbacks (comma-list) when fallback is on; just the
  // chosen type otherwise. Resize is always a single, exact type (in-family).
  const sizeArg = (): string => {
    if (!chosenType) return "";
    if (isResize || !allowFallback) return chosenType;
    const rest = (SIZE_CANDIDATES[size || ""] || []).filter((t) => t !== chosenType);
    return [chosenType, ...rest].join(",");
  };
  return (
    <Modal
      open={!!target}
      onCancel={onCancel}
      title={isResize ? `Resize ${target && "name" in target ? target.name : ""}` : "Add a host"}
      okText={isResize ? "Resize" : "Provision"}
      okButtonProps={{ disabled: !chosenType, loading: busy, danger: isResize }}
      onOk={async () => { const a = sizeArg(); if (!a) return; setBusy(true); try { await onSubmit(a, sandboxes); } finally { setBusy(false); } }}
      destroyOnHidden
    >
      <p className="mb-3 text-sm text-text-secondary">
        {isResize
          ? "An in-place resize (same instance family). The host stops, its size changes, then it starts. Sandboxes on it are interrupted."
          : "Provisions a new node from the fleet's base image and enrolls it. This takes a few minutes and runs as a background job."}
      </p>
      <Form layout="vertical">
        <Form.Item label="Instance size" required>
          <Select
            showSearch
            placeholder="Select a size"
            value={size}
            onChange={onSizeChange}
            options={sizes.map((s) => ({
              value: s.size,
              // Show the candidate Azure VM types so operators know what
              // could get created before they pick the exact one below.
              label: `${SIZE_LABEL[s.size] || s.size} · ${(SIZE_CANDIDATES[s.size] || []).map((t) => t.replace("Standard_", "")).join(" / ")} — ${s.vcpu} vCPU · ${s.memGiB} GiB${s.sandboxes ? ` · ${s.sandboxes} sandboxes` : ""}`,
            }))}
          />
        </Form.Item>
        {size && (
          <Form.Item label="VM type" required tooltip="The exact Azure VM type to provision for this size.">
            <Select
              value={chosenType}
              onChange={setChosenType}
              options={(SIZE_CANDIDATES[size] || []).map((t) => ({ value: t, label: t }))}
            />
          </Form.Item>
        )}
        {size && !isResize && (
          <Form.Item style={{ marginBottom: 12 }}>
            <Checkbox checked={allowFallback} onChange={(e) => setAllowFallback(e.target.checked)}>
              Allow fallback to the other {(SIZE_LABEL[size] || size).toLowerCase()} types if this one isn't available in the region
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item
          label={
            <Flex align="center" gap={4}>
              <span>Sandboxes per node</span>
              <HintIcon hint="Prefilled from the instance size (~2 GiB RAM per sandbox). Override if you need a different density." />
            </Flex>
          }
        >
          <InputNumber
            min={1}
            max={64}
            value={sandboxes}
            onChange={(v) => setSandboxes(typeof v === "number" ? v : undefined)}
            style={{ width: "100%" }}
            disabled={!size}
            placeholder={size ? undefined : "Select a size first"}
          />
        </Form.Item>
        {isResize && target && "current" in target && target.current && (
          // "currently" is prose, not an identifier — only the raw SKU
          // (target.current) is mono, same rule as elsewhere on the Fleet
          // page (e.g. HostCard's size-tier tag, "not connected").
          <span className="text-xs text-text-muted">
            currently <span style={{ fontFamily: MONO }}>{target.current}</span>
          </span>
        )}
      </Form>
    </Modal>
  );
}

// ── fleet settings (config caps + default size) ──────────────────────────────
function SettingsDrawer({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const [cfg, setCfg] = useState<FleetConfig | null>(null);
  const [sizes, setSizes] = useState<Size[]>([]);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();
  useEffect(() => {
    if (!open) return;
    fleetApi.config().then((c) => { setCfg(c); form.setFieldsValue({ max_nodes: c.max_nodes, max_sandboxes_per_node: c.max_sandboxes_per_node, default_size: c.default_size || undefined }); }).catch(() => {});
    fleetApi.sizes().then(setSizes).catch(() => {});
  }, [open, form]);
  const save = async () => {
    setBusy(true);
    try {
      const v = form.getFieldsValue();
      await fleetApi.putConfig(v);
      message.success("Fleet settings saved");
      onSaved();
      onClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Drawer open={open} onClose={onClose} width={420} title="Fleet settings"
      extra={<Button type="primary" loading={busy} onClick={save}>Save</Button>}>
      {cfg ? (
        <Form form={form} layout="vertical">
          <Form.Item label="Max nodes" name="max_nodes" tooltip="Hard cap on fleet size; add-host refuses past this."
            rules={[{ required: true }]}><InputNumber min={1} max={50} className="w-full" /></Form.Item>
          <Form.Item label="Sandboxes per host" name="max_sandboxes_per_node" tooltip="Placement cap fleetd enforces per host."
            rules={[{ required: true }]}><InputNumber min={1} max={10} className="w-full" /></Form.Item>
          <Form.Item label="Default new-host size" name="default_size" tooltip="Size used when Add host is submitted without one.">
            <Select allowClear showSearch placeholder="Provider default"
              options={sizes.map((s) => ({ value: s.size, label: `${s.size} — ${s.vcpu} vCPU · ${s.memGiB} GiB` }))} />
          </Form.Item>
          <p className="text-xs text-text-muted">These drive both the console and the <code>fleetctl</code> the control plane runs.</p>
        </Form>
      ) : <Empty description="Loading…" />}
    </Drawer>
  );
}

// ── SSO configuration + activation ───────────────────────────────────────────
// Read-only SSO summary — reflects the real registered provider. Editing lives
// on the Identity page (the canonical provider CRUD), so this just links there.
function SsoDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [sso, setSso] = useState<SsoConfig | null>(null);
  useEffect(() => { if (open) fleetApi.sso().then(setSso).catch(() => {}); }, [open]);
  const Row = ({ label, value, mono }: { label: string; value?: string; mono?: boolean }) => (
    <div className="flex flex-col gap-0.5 border-b border-border py-2.5 last:border-b-0">
      <span className="text-xs text-text-muted">{label}</span>
      <span className={mono ? "break-all text-[13px] text-text-primary" : "text-sm text-text-primary"} style={mono ? { fontFamily: MONO } : undefined}>{value || "—"}</span>
    </div>
  );
  return (
    <Drawer open={open} onClose={onClose} width={460} title="Single sign-on (SSO)">
      {sso ? (
        <>
          <div className="mb-4 flex items-center gap-2">
            <Tag color={sso.active ? "green" : "default"} mono>{sso.active ? "active" : "not configured"}</Tag>
            <span className="text-xs text-text-muted">{sso.active ? "Developers sign in with SSO to get an SSH cert from the fleet CA." : "No identity provider is registered yet."}</span>
          </div>
          <div className="rounded-lg border border-border bg-surface-elev px-4">
            <Row label="Provider" value={sso.provider} />
            <Row label="Tenant" value={sso.tenant} mono />
            <Row label="OIDC issuer" value={sso.issuer} mono />
            <Row label="Client ID" value={sso.client_id} mono />
            <Row label="Allowed email domains" value={sso.domains} mono />
            <Row label="Client secret" value={sso.has_client_secret ? "on file (SSM)" : "none (public PKCE)"} />
          </div>
          <div className="mt-4">
            <Link to="/identity" onClick={onClose}>
              <Button type="primary" ghost icon={<KeyRound size={15} />} block>Manage providers on Identity</Button>
            </Link>
            <p className="mt-2 text-xs text-text-muted">Add, edit, or remove SSO providers on the Identity page.</p>
          </div>
        </>
      ) : <Empty description="Loading…" />}
    </Drawer>
  );
}

// ── jobs drawer (live op log) ────────────────────────────────────────────────
function JobsDrawer({ open, jobs, onClose, token }: { open: boolean; jobs: Job[]; onClose: () => void; token: ReturnType<typeof theme.useToken>["token"] }) {
  return (
    <Drawer open={open} onClose={onClose} width={560} title="Fleet operations">
      {jobs.length === 0 ? (
        <Empty description="No operations yet" />
      ) : (
        <div className="flex flex-col gap-3">
          {jobs.map((j) => <JobRow key={j.id} job={j} token={token} />)}
        </div>
      )}
    </Drawer>
  );
}

function JobRow({ job, token }: { job: Job; token: ReturnType<typeof theme.useToken>["token"] }) {
  const [openLog, setOpenLog] = useState(activeState(job.state));
  const icon =
    job.state === "success" ? <CheckCircle2 size={15} style={{ color: token.colorSuccess }} />
    : job.state === "failed" ? <XCircle size={15} style={{ color: token.colorError }} />
    : <Loader2 size={15} className="animate-spin" style={{ color: token.colorPrimary }} />;
  return (
    <div className="rounded-lg border border-border bg-surface-elev">
      <button className="flex w-full items-center gap-2 px-3 py-2 text-left" onClick={() => setOpenLog((v) => !v)}>
        {icon}
        <span className="text-sm font-medium text-text-primary">{job.label}</span>
        <Tag className="ml-auto" mono
          color={job.state === "success" ? "green" : job.state === "failed" ? "red" : "blue"}>{job.state}</Tag>
      </button>
      {openLog && (
        <pre className="max-h-56 overflow-auto border-t border-border px-3 py-2 text-[11.5px] leading-relaxed text-text-secondary"
          style={{ fontFamily: MONO, whiteSpace: "pre-wrap" }}>
          {(job.log && job.log.length ? job.log : ["(no output yet)"]).join("\n")}
        </pre>
      )}
    </div>
  );
}

// small helper so the page can render a consistent per-host action set.
export { Plus, Settings, ShieldCheck, ListChecks, Trash2 };
